import * as d3 from 'd3';
import {geoNaturalEarth1, geoPath} from 'd3-geo';
import React, {useEffect, useRef} from 'react';
import {feature} from 'topojson-client';
import type {GeometryCollection, Topology} from 'topojson-specification';

interface LocationMiniMapProps {
  lat: number;
  lon: number;
  height?: number;
}

const WORLD_URL = '/world-atlas-110m.json';

export default function LocationMiniMap({lat, lon, height = 200}: LocationMiniMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current || !containerRef.current) return;

    const containerWidth = containerRef.current.clientWidth || 300;

    fetch(WORLD_URL)
      .then((res) => res.json())
      .then((world: Topology<{countries: GeometryCollection}>) => {
        if (!svgRef.current) return;

        const countries = feature(world, world.objects.countries);

        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();

        svg
          .attr('viewBox', `0 0 ${containerWidth} ${height}`)
          .attr('preserveAspectRatio', 'xMidYMid meet');

        const projection = geoNaturalEarth1()
          .fitSize([containerWidth - 20, height - 20], countries as any)
          .translate([containerWidth / 2, height / 2]);

        const path = geoPath().projection(projection);

        // Draw countries
        svg
          .append('g')
          .selectAll('path')
          .data((countries as any).features)
          .join('path')
          .attr('d', path as any)
          .attr('fill', '#f5f5f5')
          .attr('stroke', '#ccc')
          .attr('stroke-width', 0.5);

        // Draw location marker
        const coords = projection([lon, lat]);
        if (coords) {
          const [px, py] = coords;

          // Outer pulsing circle
          svg
            .append('circle')
            .attr('cx', px)
            .attr('cy', py)
            .attr('r', 12)
            .attr('fill', '#2563eb')
            .attr('opacity', 0.3);

          // Inner solid circle
          svg
            .append('circle')
            .attr('cx', px)
            .attr('cy', py)
            .attr('r', 6)
            .attr('fill', '#2563eb')
            .attr('stroke', '#fff')
            .attr('stroke-width', 2);
        }
      })
      .catch((err) => {
        console.error('Failed to load world map:', err);
        if (containerRef.current) {
          containerRef.current.innerHTML = `
            <div class="flex items-center justify-center h-full text-gray-500 text-lg">
              ${Math.abs(lat).toFixed(2)}\u00b0${lat >= 0 ? 'N' : 'S'}, ${Math.abs(lon).toFixed(2)}\u00b0${lon >= 0 ? 'E' : 'W'}
            </div>
          `;
        }
      });
  }, [lat, lon, height]);

  return (
    <div
      ref={containerRef}
      className="w-full border border-gray-200 bg-gray-50 flex items-center justify-center"
      style={{minHeight: height}}
    >
      <svg ref={svgRef} className="w-full" style={{height}} />
    </div>
  );
}
